import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { adminConfigApi, areaPositionApi, inventoryApi, medallionColorApi } from '../../services/api'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'

/**
 * Admin Config — single-row settings for the area + position & color list management.
 *
 * Five sections:
 *   1. Logo upload — has a preview-before-save UX
 *   2. Text fields — area name, addresses, contact info, welcome message
 *   3. Shop category order — drag-to-reorder list driving the Shop page
 *   4. Area Officer Positions — list management for the position dropdown
 *   5. Medallion Colors — list management for the Triplate color dropdown
 *
 * Each section saves independently because they're separate endpoints
 * and serializing them would be a worse user experience.
 */
export default function AdminConfig() {
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)

    const [form, setForm] = useState(emptyForm())

    // Server-side state — what's actually saved
    const [savedLogoUrl, setSavedLogoUrl] = useState(null)
    const [savedCategorySortOrder, setSavedCategorySortOrder] = useState(null)

    useEffect(() => {
        load()
    }, [])

    async function load() {
        setLoading(true)
        try {
            const response = await adminConfigApi.get()
            if (response.success) {
                const c = response.config
                setForm({
                    areaName: c.areaName || '',
                    areaAddress: c.areaAddress || '',
                    areaShippingAddress: c.areaShippingAddress || '',
                    mapLink: c.mapLink || '',
                    welcomeMessage: c.welcomeMessage || '',
                    datesTimes: c.datesTimes || '',
                    pickupMessageOverride: c.pickupMessageOverride || '',
                    pickupMessageOverrideUntil: c.pickupMessageOverrideUntil
                        ? c.pickupMessageOverrideUntil.slice(0, 10)
                        : '',
                    chairName: c.chairName || '',
                    literatureEmail: c.literatureEmail || '',
                    regionalLitEmail: c.regionalLitEmail || '',
                    secretaryEmail: c.secretaryEmail || '',
                    phone: c.phone || '',
                })
                setSavedLogoUrl(c.logoUrl || null)
                setSavedCategorySortOrder(c.categorySortOrder || null)
            } else {
                toast.error(response.message || 'Could not load config')
            }
        } catch {
            toast.error('Network error loading config')
        } finally {
            setLoading(false)
        }
    }

    function update(field, value) {
        setForm(f => ({ ...f, [field]: value }))
    }

    async function handleSaveText(e) {
        e.preventDefault()
        setSaving(true)
        try {
            const payload = {
                ...form,
                pickupMessageOverrideUntil: form.pickupMessageOverrideUntil || null,
                // Include the saved sort order so we don't accidentally clear it
                // when saving text fields. The dedicated section handles edits.
                categorySortOrder: savedCategorySortOrder,
            }
            const response = await adminConfigApi.update(payload)
            if (response.success) {
                toast.success('Settings saved')
            } else {
                toast.error(response.message || 'Save failed')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return <div className="p-8 text-center text-gray-500">Loading config...</div>
    }

    return (
        <div className="space-y-6 max-w-3xl">
            <div>
                <h2 className="text-2xl font-semibold text-ghasc-navy">Admin Config</h2>
                <p className="text-sm text-gray-500 mt-1">
                    Area-wide settings. The welcome message is shown on every user's
                    dashboard. Email addresses are used by the system to route notifications.
                </p>
            </div>

            {/* ----- Logo section ------------------------------------------------ */}
            <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
                <h3 className="text-lg font-semibold text-ghasc-navy">Area Logo</h3>
                <LogoUploader
                    savedLogoUrl={savedLogoUrl}
                    onChange={setSavedLogoUrl}
                />
            </div>

            {/* ----- Text fields form ------------------------------------------- */}
            <form onSubmit={handleSaveText} className="bg-white rounded-xl shadow-sm p-6 space-y-4">
                <h3 className="text-lg font-semibold text-ghasc-navy">Area Information</h3>

                <Input id="cf-area-name" label="Area name"
                    value={form.areaName} onChange={(e) => update('areaName', e.target.value)} />

                <Textarea id="cf-welcome" label="Welcome message"
                    value={form.welcomeMessage} onChange={(e) => update('welcomeMessage', e.target.value)}
                    hint="Shown on every user's dashboard." rows={3} />

                <Textarea id="cf-dates" label="Meeting dates / times"
                    value={form.datesTimes} onChange={(e) => update('datesTimes', e.target.value)} rows={2} />

                <Textarea id="cf-area-address" label="Area meeting address"
                    value={form.areaAddress} onChange={(e) => update('areaAddress', e.target.value)} rows={2}
                    hint="The area's general location/address. Informational." />

                <Textarea id="cf-area-ship" label="Area shipping address"
                    value={form.areaShippingAddress}
                    onChange={(e) => update('areaShippingAddress', e.target.value)} rows={3}
                    hint="Destination address used on regional reorder forms (where Ontario region ships to)." />

                <Input id="cf-map" label="Google Maps link (optional)"
                    value={form.mapLink} onChange={(e) => update('mapLink', e.target.value)}
                    placeholder="https://maps.google.com/..." />

                <div className="border-t border-gray-100 pt-4">
                    <p className="text-sm font-medium text-gray-700 mb-1">Pickup deadline message (override)</p>
                    <p className="text-xs text-gray-500 mb-3">
                        The dashboard normally shows an auto-computed deadline based on the cycle calendar
                        (2nd Sunday of the month, 1st Sunday in May/October, Friday 3 PM cutoff).
                        Use the override only for special weeks. Set an expiry date to auto-clear it.
                    </p>
                    <Textarea
                        id="cf-pickup-override"
                        label="Override message (leave blank to use auto-computed)"
                        value={form.pickupMessageOverride}
                        onChange={(e) => update('pickupMessageOverride', e.target.value)}
                        rows={2}
                        hint="Example: 'Special: orders due by Wednesday June 10, 5 PM this week only.'"
                    />
                    <div className="mt-3">
                        <label htmlFor="cf-pickup-until" className="block text-sm font-medium text-gray-700 mb-1">
                            Auto-clear on (optional)
                        </label>
                        <input
                            id="cf-pickup-until"
                            type="date"
                            value={form.pickupMessageOverrideUntil}
                            onChange={(e) => update('pickupMessageOverrideUntil', e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                            After this date, the override is ignored and the auto-computed message returns.
                            Leave blank to keep the override until manually cleared.
                        </p>
                    </div>
                </div>

                <h3 className="text-lg font-semibold text-ghasc-navy pt-4 border-t border-gray-100">
                    Contact
                </h3>

                <div className="grid sm:grid-cols-2 gap-3">
                    <Input id="cf-chair" label="Literature chair name"
                        value={form.chairName} onChange={(e) => update('chairName', e.target.value)} />
                    <Input id="cf-phone" label="Phone"
                        value={form.phone} onChange={(e) => update('phone', e.target.value)} />
                </div>

                <Input id="cf-lit-email" label="Literature email (e-transfer destination)"
                    type="email" value={form.literatureEmail}
                    onChange={(e) => update('literatureEmail', e.target.value)} />

                <div className="grid sm:grid-cols-2 gap-3">
                    <Input id="cf-reg-email" label="Regional supplier email"
                        type="email" value={form.regionalLitEmail}
                        onChange={(e) => update('regionalLitEmail', e.target.value)} />
                    <Input id="cf-sec-email" label="Secretary email"
                        type="email" value={form.secretaryEmail}
                        onChange={(e) => update('secretaryEmail', e.target.value)} />
                </div>

                <div className="flex justify-end pt-2">
                    <Button type="submit" loading={saving}>Save Settings</Button>
                </div>
            </form>

            {/* ----- Shop Category Order ---------------------------------------- */}
            <CategoryOrderSection
                savedOrder={savedCategorySortOrder}
                onSaved={(newOrder) => setSavedCategorySortOrder(newOrder)}
                currentFormSnapshot={form}
            />

            {/* ----- Area Officer Positions ------------------------------------- */}
            <AreaPositionsSection />

            {/* ----- Medallion Colors ------------------------------------------- */}
            <MedallionColorsSection />
        </div>
    )
}

// ---------------------------------------------------------------------------
//  Shop Category Order
// ---------------------------------------------------------------------------
/**
 * Drag-to-reorder + up/down buttons. Categories not yet ordered appear in
 * an "unordered" pool below the ordered list and can be added by clicking
 * "Add to order". Drag-and-drop is the main interaction; the buttons are
 * an accessible fallback (and useful on mobile where drag is awkward).
 */
function CategoryOrderSection({ savedOrder, onSaved, currentFormSnapshot }) {
    const [allCategories, setAllCategories] = useState([])
    const [orderedList, setOrderedList] = useState([])     // current edit state
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [draggedIndex, setDraggedIndex] = useState(null)

    useEffect(() => {
        loadCategories()
    }, [])

    // Keep local state in sync with the savedOrder prop (e.g. after a save)
    useEffect(() => {
        if (savedOrder && savedOrder.length > 0) {
            setOrderedList(savedOrder)
        } else {
            setOrderedList([])
        }
    }, [savedOrder])

    async function loadCategories() {
        setLoading(true)
        try {
            const response = await inventoryApi.listCategories()
            if (response.success) {
                setAllCategories(response.categories || [])
            }
        } catch {
            // non-fatal
        } finally {
            setLoading(false)
        }
    }

    // Categories that exist in inventory but aren't in the saved/edit order
    const unordered = allCategories
        .filter(c => !orderedList.includes(c))
        .sort((a, b) => a.localeCompare(b))

    // Categories in the order list that no longer exist in inventory (stale)
    const stale = orderedList.filter(c => !allCategories.includes(c))

    function moveUp(index) {
        if (index <= 0) return
        const next = [...orderedList]
            ;[next[index - 1], next[index]] = [next[index], next[index - 1]]
        setOrderedList(next)
    }

    function moveDown(index) {
        if (index >= orderedList.length - 1) return
        const next = [...orderedList]
            ;[next[index], next[index + 1]] = [next[index + 1], next[index]]
        setOrderedList(next)
    }

    function removeFromOrder(category) {
        setOrderedList(orderedList.filter(c => c !== category))
    }

    function addToOrder(category) {
        setOrderedList([...orderedList, category])
    }

    function addAllRemaining() {
        setOrderedList([...orderedList, ...unordered])
    }

    function resetToAlphabetical() {
        if (!confirm('Clear the custom order and use plain alphabetical?')) return
        setOrderedList([])
    }

    // ---- Drag and drop ----
    function handleDragStart(e, index) {
        setDraggedIndex(index)
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', String(index))
    }

    function handleDragOver(e) {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
    }

    function handleDrop(e, dropIndex) {
        e.preventDefault()
        if (draggedIndex === null || draggedIndex === dropIndex) {
            setDraggedIndex(null)
            return
        }
        const next = [...orderedList]
        const [moved] = next.splice(draggedIndex, 1)
        next.splice(dropIndex, 0, moved)
        setOrderedList(next)
        setDraggedIndex(null)
    }

    function handleDragEnd() {
        setDraggedIndex(null)
    }

    async function handleSave() {
        setSaving(true)
        try {
            // Send the full config payload — server expects the complete
            // UpdateAdminConfigRequest. Include current form values so we
            // don't clobber any text-field edits the user has made but
            // hasn't yet saved via the main form.
            const payload = {
                ...currentFormSnapshot,
                pickupMessageOverrideUntil: currentFormSnapshot.pickupMessageOverrideUntil || null,
                categorySortOrder: orderedList.length > 0 ? orderedList : null,
            }
            const response = await adminConfigApi.update(payload)
            if (response.success) {
                toast.success('Category order saved')
                onSaved(orderedList.length > 0 ? orderedList : null)
            } else {
                toast.error(response.message || 'Save failed')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setSaving(false)
        }
    }

    const hasChanges = JSON.stringify(orderedList) !== JSON.stringify(savedOrder || [])

    return (
        <div className="bg-white rounded-xl shadow-sm">
            <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-ghasc-navy">Shop Category Order</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                    Drag to reorder, or use the ↑ / ↓ buttons. The Shop page shows categories in this order.
                    Categories not listed here fall to the bottom alphabetically.
                </p>
            </div>

            <div className="p-6 space-y-4">
                {loading ? (
                    <div className="p-4 text-center text-gray-500">Loading…</div>
                ) : (
                    <>
                        {orderedList.length === 0 ? (
                            <div className="rounded-lg bg-gray-50 border border-dashed border-gray-300 p-4 text-center text-sm text-gray-500">
                                No custom order set — Shop will show categories in alphabetical order.
                                Click "Add to order" on the categories below to start customizing.
                            </div>
                        ) : (
                            <ol className="space-y-1">
                                {orderedList.map((category, index) => {
                                    const isStale = !allCategories.includes(category)
                                    return (
                                        <li
                                            key={category}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, index)}
                                            onDragOver={handleDragOver}
                                            onDrop={(e) => handleDrop(e, index)}
                                            onDragEnd={handleDragEnd}
                                            className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${draggedIndex === index
                                                    ? 'opacity-50 border-ghasc-navy'
                                                    : isStale
                                                        ? 'border-amber-200 bg-amber-50'
                                                        : 'border-gray-200 hover:border-ghasc-navy bg-white'
                                                } cursor-move`}
                                        >
                                            <span className="text-gray-400 text-sm select-none" title="Drag to reorder">⋮⋮</span>
                                            <span className="text-xs font-mono text-gray-500 w-6 text-right">{index + 1}.</span>
                                            <span className="flex-1 text-sm">
                                                {category}
                                                {isStale && (
                                                    <span className="ml-2 text-xs text-amber-700">
                                                        (no items in this category)
                                                    </span>
                                                )}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => moveUp(index)}
                                                disabled={index === 0}
                                                className="w-7 h-7 flex items-center justify-center rounded text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                                                title="Move up"
                                                aria-label="Move up"
                                            >↑</button>
                                            <button
                                                type="button"
                                                onClick={() => moveDown(index)}
                                                disabled={index === orderedList.length - 1}
                                                className="w-7 h-7 flex items-center justify-center rounded text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                                                title="Move down"
                                                aria-label="Move down"
                                            >↓</button>
                                            <button
                                                type="button"
                                                onClick={() => removeFromOrder(category)}
                                                className="text-xs text-gray-500 hover:text-ghasc-danger hover:underline ml-1"
                                                title="Remove from custom order"
                                            >Remove</button>
                                        </li>
                                    )
                                })}
                            </ol>
                        )}

                        {/* Unordered pool */}
                        {unordered.length > 0 && (
                            <div className="pt-2 border-t border-gray-100">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-sm font-medium text-gray-700">
                                        Not in custom order ({unordered.length})
                                    </p>
                                    <button
                                        type="button"
                                        onClick={addAllRemaining}
                                        className="text-xs text-ghasc-navy hover:underline"
                                    >
                                        Add all to end
                                    </button>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {unordered.map(c => (
                                        <button
                                            key={c}
                                            type="button"
                                            onClick={() => addToOrder(c)}
                                            className="text-xs px-2 py-1 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200"
                                            title="Add to custom order"
                                        >
                                            {c} <span className="text-gray-400 ml-1">+</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Stale entries warning (if any in the saved order are no longer used) */}
                        {stale.length > 0 && (
                            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                                <strong>Note:</strong> {stale.length} categor{stale.length === 1 ? 'y is' : 'ies are'} in
                                the custom order but no longer have items in inventory. They'll be ignored on the Shop
                                page. You can leave them in case the category comes back, or click "Remove" above.
                            </div>
                        )}

                        <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                            <button
                                type="button"
                                onClick={resetToAlphabetical}
                                disabled={orderedList.length === 0}
                                className="text-sm text-gray-500 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Reset to alphabetical
                            </button>
                            <Button
                                type="button"
                                onClick={handleSave}
                                loading={saving}
                                disabled={!hasChanges}
                            >
                                Save Order
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

// ---------------------------------------------------------------------------
//  Logo uploader with preview-before-save
// ---------------------------------------------------------------------------
function LogoUploader({ savedLogoUrl, onChange }) {
    const fileInputRef = useRef(null)
    const [pendingFile, setPendingFile] = useState(null)
    const [pendingPreviewUrl, setPendingPreviewUrl] = useState(null)
    const [busy, setBusy] = useState(false)

    useEffect(() => {
        return () => {
            if (pendingPreviewUrl) URL.revokeObjectURL(pendingPreviewUrl)
        }
    }, [pendingPreviewUrl])

    function handleFilePick(e) {
        const file = e.target.files?.[0]
        if (!file) return

        if (!file.type.startsWith('image/')) {
            toast.error('Please choose an image file')
            return
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Image must be smaller than 5 MB')
            return
        }

        if (pendingPreviewUrl) URL.revokeObjectURL(pendingPreviewUrl)
        setPendingFile(file)
        setPendingPreviewUrl(URL.createObjectURL(file))
    }

    function handleCancel() {
        if (pendingPreviewUrl) URL.revokeObjectURL(pendingPreviewUrl)
        setPendingFile(null)
        setPendingPreviewUrl(null)
        if (fileInputRef.current) fileInputRef.current.value = ''
    }

    async function handleSave() {
        if (!pendingFile) return

        setBusy(true)
        try {
            const response = await adminConfigApi.uploadLogo(pendingFile)
            if (response.success) {
                toast.success('Logo updated')
                onChange(response.logoUrl)
                handleCancel()
            } else {
                toast.error(response.message || 'Upload failed')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setBusy(false)
        }
    }

    async function handleDeleteCurrent() {
        if (!confirm('Remove the current logo?')) return
        setBusy(true)
        try {
            const response = await adminConfigApi.deleteLogo()
            if (response.success) {
                toast.success('Logo removed')
                onChange(null)
            } else {
                toast.error(response.message || 'Delete failed')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setBusy(false)
        }
    }

    if (pendingFile) {
        return (
            <div className="space-y-3">
                <div className="text-sm text-gray-600">Preview:</div>
                <div className="flex items-center justify-center bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <img src={pendingPreviewUrl} alt="Pending logo preview"
                        className="max-h-48 max-w-full" />
                </div>
                <div className="text-sm text-gray-500">
                    {pendingFile.name} — {(pendingFile.size / 1024).toFixed(1)} KB
                </div>
                <div className="flex justify-end gap-2">
                    <Button type="button" variant="secondary" onClick={handleCancel} disabled={busy}>
                        Cancel
                    </Button>
                    <Button type="button" onClick={handleSave} loading={busy}>
                        Save Logo
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-3">
            {savedLogoUrl ? (
                <div className="space-y-3">
                    <div className="flex items-center justify-center bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <img src={savedLogoUrl} alt="Current logo" className="max-h-48 max-w-full" />
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button type="button" variant="secondary" onClick={() => fileInputRef.current?.click()} disabled={busy}>
                            Replace
                        </Button>
                        <Button type="button" variant="danger" onClick={handleDeleteCurrent} disabled={busy}>
                            Remove
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center bg-gray-50 rounded-lg p-8 border-2 border-dashed border-gray-300">
                    <p className="text-sm text-gray-500 mb-3">No logo uploaded.</p>
                    <Button type="button" onClick={() => fileInputRef.current?.click()} disabled={busy}>
                        Upload Logo
                    </Button>
                </div>
            )}

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFilePick}
                className="hidden"
            />

            <p className="text-xs text-gray-500">
                PNG, JPG, GIF, WebP, or SVG. Max 5 MB.
            </p>
        </div>
    )
}

// ---------------------------------------------------------------------------
//  Textarea helper
// ---------------------------------------------------------------------------
function Textarea({ id, label, value, onChange, hint, rows = 3 }) {
    return (
        <div>
            <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
                {label}
            </label>
            <textarea
                id={id}
                value={value}
                onChange={onChange}
                rows={rows}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy focus:border-transparent"
            />
            {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
        </div>
    )
}

function emptyForm() {
    return {
        areaName: '',
        areaAddress: '',
        areaShippingAddress: '',
        mapLink: '',
        welcomeMessage: '',
        datesTimes: '',
        pickupMessageOverride: '',
        pickupMessageOverrideUntil: '',
        chairName: '',
        literatureEmail: '',
        regionalLitEmail: '',
        secretaryEmail: '',
        phone: '',
    }
}

// ===========================================================================
//  Area Officer Positions section
// ===========================================================================
function AreaPositionsSection() {
    return (
        <ListManagerSection
            title="Area Officer Positions"
            description="Standard service committee positions. Used in dropdowns when assigning area-admin users and when officers submit monthly reports."
            api={areaPositionApi}
            itemNounSingular="position"
            itemNounPlural="positions"
            newPlaceholder="e.g. Outreach Co-Chair"
        />
    )
}

// ===========================================================================
//  Medallion Colors section
// ===========================================================================
function MedallionColorsSection() {
    const [colors, setColors] = useState([])
    const [loading, setLoading] = useState(true)
    const [showInactive, setShowInactive] = useState(false)
    const [newName, setNewName] = useState('')
    const [newCode, setNewCode] = useState('')
    const [adding, setAdding] = useState(false)
    const [editingId, setEditingId] = useState(null)
    const [editDraft, setEditDraft] = useState({ name: '', orderCode: '', sortOrder: 0 })

    useEffect(() => { load() }, [showInactive])

    async function load() {
        setLoading(true)
        try {
            const response = await medallionColorApi.list(showInactive)
            if (response.success) setColors(response.colors)
            else toast.error(response.message || 'Could not load colors')
        } catch {
            toast.error('Network error')
        } finally {
            setLoading(false)
        }
    }

    async function handleAdd() {
        const name = newName.trim()
        if (!name) return
        setAdding(true)
        try {
            const response = await medallionColorApi.create({
                name,
                orderCode: newCode.trim() || null,
            })
            if (response.success) {
                toast.success('Color added')
                setNewName('')
                setNewCode('')
                load()
            } else {
                toast.error(response.message || 'Could not add color')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setAdding(false)
        }
    }

    function startEdit(c) {
        setEditingId(c.id)
        setEditDraft({
            name: c.name,
            orderCode: c.orderCode || '',
            sortOrder: c.sortOrder,
        })
    }

    function cancelEdit() {
        setEditingId(null)
        setEditDraft({ name: '', orderCode: '', sortOrder: 0 })
    }

    async function saveEdit(c) {
        const name = editDraft.name.trim()
        if (!name) {
            toast.error('Display name is required')
            return
        }
        try {
            const response = await medallionColorApi.update(c.id, {
                name,
                orderCode: editDraft.orderCode.trim() || null,
                sortOrder: Number(editDraft.sortOrder) || 0,
            })
            if (response.success) {
                toast.success('Color updated')
                cancelEdit()
                load()
            } else {
                toast.error(response.message || 'Update failed')
            }
        } catch {
            toast.error('Network error')
        }
    }

    async function handleToggleActive(c) {
        try {
            if (c.active) {
                const response = await medallionColorApi.deactivate(c.id)
                if (response.success) {
                    toast.success(`"${c.name}" deactivated`)
                    load()
                } else {
                    toast.error(response.message || 'Could not deactivate')
                }
            } else {
                const response = await medallionColorApi.update(c.id, {
                    name: c.name,
                    orderCode: c.orderCode,
                    active: true,
                })
                if (response.success) {
                    toast.success(`"${c.name}" reactivated`)
                    load()
                } else {
                    toast.error(response.message || 'Could not reactivate')
                }
            }
        } catch {
            toast.error('Network error')
        }
    }

    return (
        <div className="bg-white rounded-xl shadow-sm">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h3 className="text-lg font-semibold text-ghasc-navy">Medallion Colors</h3>
                    <p className="text-sm text-gray-500 mt-0.5">
                        Color/finish options for Triplate medallions.
                        <span className="block mt-0.5">
                            <span className="font-medium">Display Name</span> — shown to users on the Shop page.{' '}
                            <span className="font-medium">Order Code</span> — used on regional reorder forms.
                        </span>
                    </p>
                </div>
                <label className="flex items-center text-sm text-gray-700">
                    <input
                        type="checkbox"
                        checked={showInactive}
                        onChange={(e) => setShowInactive(e.target.checked)}
                        className="mr-2"
                    />
                    Show inactive
                </label>
            </div>

            <div className="p-6 space-y-4">
                <div className="grid sm:grid-cols-[1fr_140px_auto] gap-2 items-end">
                    <Input
                        id="new-color-name"
                        label="Add a new color (display name)"
                        placeholder="e.g. Gold-Pearl-Silver"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAdd() }}
                    />
                    <Input
                        id="new-color-code"
                        label="Order code"
                        placeholder="e.g. GOLD"
                        value={newCode}
                        onChange={(e) => setNewCode(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAdd() }}
                    />
                    <Button onClick={handleAdd} loading={adding} disabled={!newName.trim()}>
                        Add
                    </Button>
                </div>

                {loading ? (
                    <div className="p-6 text-center text-gray-500">Loading...</div>
                ) : colors.length === 0 ? (
                    <div className="p-6 text-center text-gray-500">No colors configured.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                <tr>
                                    <th className="px-3 py-2 w-16">Order</th>
                                    <th className="px-3 py-2">Display Name</th>
                                    <th className="px-3 py-2 w-32">Order Code</th>
                                    <th className="px-3 py-2 w-16 text-center">Active</th>
                                    <th className="px-3 py-2 w-32 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {colors.map(c => (
                                    <tr key={c.id} className={c.active ? 'hover:bg-gray-50' : 'bg-gray-50 text-gray-500'}>
                                        {editingId === c.id ? (
                                            <>
                                                <td className="px-3 py-2">
                                                    <input
                                                        type="number"
                                                        value={editDraft.sortOrder}
                                                        onChange={(e) => setEditDraft(d => ({ ...d, sortOrder: e.target.value }))}
                                                        className="w-14 px-2 py-1 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                                                    />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <input
                                                        type="text"
                                                        value={editDraft.name}
                                                        onChange={(e) => setEditDraft(d => ({ ...d, name: e.target.value }))}
                                                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                                                        autoFocus
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') saveEdit(c)
                                                            if (e.key === 'Escape') cancelEdit()
                                                        }}
                                                    />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <input
                                                        type="text"
                                                        value={editDraft.orderCode}
                                                        onChange={(e) => setEditDraft(d => ({ ...d, orderCode: e.target.value }))}
                                                        placeholder="—"
                                                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') saveEdit(c)
                                                            if (e.key === 'Escape') cancelEdit()
                                                        }}
                                                    />
                                                </td>
                                                <td className="px-3 py-2 text-center text-xs text-gray-500">
                                                    {c.active ? 'Yes' : 'No'}
                                                </td>
                                                <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                                                    <button onClick={() => saveEdit(c)} className="text-ghasc-navy hover:underline text-sm">Save</button>
                                                    <button onClick={cancelEdit} className="text-gray-500 hover:underline text-sm">Cancel</button>
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td className="px-3 py-2 text-sm font-mono">{c.sortOrder}</td>
                                                <td className="px-3 py-2">{c.name}</td>
                                                <td className="px-3 py-2 text-sm font-mono text-gray-700">
                                                    {c.orderCode || <span className="text-gray-400">—</span>}
                                                </td>
                                                <td className="px-3 py-2 text-center text-xs">
                                                    {c.active
                                                        ? <span className="text-ghasc-success">✓</span>
                                                        : <span className="text-gray-400">—</span>}
                                                </td>
                                                <td className="px-3 py-2 text-right space-x-3 whitespace-nowrap">
                                                    <button onClick={() => startEdit(c)} className="text-ghasc-navy hover:underline text-sm">Edit</button>
                                                    <button onClick={() => handleToggleActive(c)} className="text-gray-600 hover:underline text-sm">
                                                        {c.active ? 'Deactivate' : 'Reactivate'}
                                                    </button>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    )
}

// ===========================================================================
//  Generic list manager
// ===========================================================================
function ListManagerSection({
    title,
    description,
    api,
    itemNounSingular,
    itemNounPlural,
    newPlaceholder,
    listKey = 'positions',
}) {
    const [items, setItems] = useState([])
    const [loading, setLoading] = useState(true)
    const [showInactive, setShowInactive] = useState(false)
    const [newName, setNewName] = useState('')
    const [adding, setAdding] = useState(false)
    const [editingId, setEditingId] = useState(null)
    const [editDraft, setEditDraft] = useState({ name: '', sortOrder: 0 })

    useEffect(() => { load() }, [showInactive])

    async function load() {
        setLoading(true)
        try {
            const response = await api.list(showInactive)
            if (response.success) setItems(response[listKey] || [])
            else toast.error(response.message || `Could not load ${itemNounPlural}`)
        } catch {
            toast.error('Network error')
        } finally {
            setLoading(false)
        }
    }

    async function handleAdd() {
        const name = newName.trim()
        if (!name) return
        setAdding(true)
        try {
            const response = await api.create({ name })
            if (response.success) {
                toast.success(`${capitalize(itemNounSingular)} added`)
                setNewName('')
                load()
            } else {
                toast.error(response.message || `Could not add ${itemNounSingular}`)
            }
        } catch {
            toast.error('Network error')
        } finally {
            setAdding(false)
        }
    }

    function startEdit(item) {
        setEditingId(item.id)
        setEditDraft({ name: item.name, sortOrder: item.sortOrder })
    }

    function cancelEdit() {
        setEditingId(null)
        setEditDraft({ name: '', sortOrder: 0 })
    }

    async function saveEdit(item) {
        const name = editDraft.name.trim()
        if (!name) {
            toast.error('Name is required')
            return
        }
        try {
            const response = await api.update(item.id, {
                name,
                sortOrder: Number(editDraft.sortOrder) || 0,
            })
            if (response.success) {
                toast.success(`${capitalize(itemNounSingular)} updated`)
                cancelEdit()
                load()
            } else {
                toast.error(response.message || 'Update failed')
            }
        } catch {
            toast.error('Network error')
        }
    }

    async function handleToggleActive(item) {
        try {
            if (item.active) {
                const response = await api.deactivate(item.id)
                if (response.success) {
                    toast.success(`"${item.name}" deactivated`)
                    load()
                } else {
                    toast.error(response.message || 'Could not deactivate')
                }
            } else {
                const response = await api.update(item.id, {
                    name: item.name,
                    active: true,
                })
                if (response.success) {
                    toast.success(`"${item.name}" reactivated`)
                    load()
                } else {
                    toast.error(response.message || 'Could not reactivate')
                }
            }
        } catch {
            toast.error('Network error')
        }
    }

    return (
        <div className="bg-white rounded-xl shadow-sm">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h3 className="text-lg font-semibold text-ghasc-navy">{title}</h3>
                    <p className="text-sm text-gray-500 mt-0.5">{description}</p>
                </div>
                <label className="flex items-center text-sm text-gray-700">
                    <input
                        type="checkbox"
                        checked={showInactive}
                        onChange={(e) => setShowInactive(e.target.checked)}
                        className="mr-2"
                    />
                    Show inactive
                </label>
            </div>

            <div className="p-6 space-y-4">
                <div className="flex gap-2 items-end">
                    <div className="flex-1">
                        <Input
                            id={`new-${itemNounSingular}-name`}
                            label={`Add a new ${itemNounSingular}`}
                            placeholder={newPlaceholder}
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleAdd() }}
                        />
                    </div>
                    <Button onClick={handleAdd} loading={adding} disabled={!newName.trim()}>
                        Add
                    </Button>
                </div>

                {loading ? (
                    <div className="p-6 text-center text-gray-500">Loading...</div>
                ) : items.length === 0 ? (
                    <div className="p-6 text-center text-gray-500">No {itemNounPlural} configured.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                <tr>
                                    <th className="px-3 py-2 w-20">Order</th>
                                    <th className="px-3 py-2">Name</th>
                                    <th className="px-3 py-2 w-20 text-center">Active</th>
                                    <th className="px-3 py-2 w-32 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {items.map(item => (
                                    <tr key={item.id} className={item.active ? 'hover:bg-gray-50' : 'bg-gray-50 text-gray-500'}>
                                        {editingId === item.id ? (
                                            <>
                                                <td className="px-3 py-2">
                                                    <input
                                                        type="number"
                                                        value={editDraft.sortOrder}
                                                        onChange={(e) => setEditDraft(d => ({ ...d, sortOrder: e.target.value }))}
                                                        className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                                                    />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <input
                                                        type="text"
                                                        value={editDraft.name}
                                                        onChange={(e) => setEditDraft(d => ({ ...d, name: e.target.value }))}
                                                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                                                        autoFocus
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') saveEdit(item)
                                                            if (e.key === 'Escape') cancelEdit()
                                                        }}
                                                    />
                                                </td>
                                                <td className="px-3 py-2 text-center text-xs text-gray-500">
                                                    {item.active ? 'Yes' : 'No'}
                                                </td>
                                                <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                                                    <button onClick={() => saveEdit(item)} className="text-ghasc-navy hover:underline text-sm">Save</button>
                                                    <button onClick={cancelEdit} className="text-gray-500 hover:underline text-sm">Cancel</button>
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td className="px-3 py-2 text-sm font-mono">{item.sortOrder}</td>
                                                <td className="px-3 py-2">{item.name}</td>
                                                <td className="px-3 py-2 text-center text-xs">
                                                    {item.active
                                                        ? <span className="text-ghasc-success">✓</span>
                                                        : <span className="text-gray-400">—</span>}
                                                </td>
                                                <td className="px-3 py-2 text-right space-x-3 whitespace-nowrap">
                                                    <button onClick={() => startEdit(item)} className="text-ghasc-navy hover:underline text-sm">Edit</button>
                                                    <button onClick={() => handleToggleActive(item)} className="text-gray-600 hover:underline text-sm">
                                                        {item.active ? 'Deactivate' : 'Reactivate'}
                                                    </button>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    )
}

function capitalize(s) {
    return s.charAt(0).toUpperCase() + s.slice(1)
}